import { AuthenticationResult } from "@azure/msal-browser";
import { appInsightsTracker } from "utils/AppInsightsManager/appInsights.utils";
import { loginRequest, msalInstance } from "utils/azureAD/authConfig";

export const getAccessToken = async (): Promise<string | undefined> => {
  const request = {
    ...loginRequest,
    account: msalInstance.getAllAccounts()[0],
  };

  try {
    const { accessToken }: AuthenticationResult = await msalInstance.acquireTokenSilent(request);
    return accessToken;
  } catch (_) {
    await msalInstance.acquireTokenRedirect(request);
    return undefined;
  }
};

export const getAuthorizationHeader = async (): Promise<Headers> => {
  const headers = new Headers();

  try {
    const accessToken = await getAccessToken();
    if (accessToken) {
      headers.append("Authorization", `Bearer ${accessToken}`);
    }
  } catch (error) {
    console.error("Error getting authorization header:", error);
  } finally {
    return headers;
  }
};

function fetchRequest(method: string) {
  return async (url: string, body?: any) => {
    
    const headers = navigator.onLine
      ? await getAuthorizationHeader()
      : new Headers();
    const requestOptions: RequestInit = { method };

    if (body) {
      headers.append("Content-Type", "application/json");
      requestOptions.body = JSON.stringify(body);
    }

    return fetch(url, { ...requestOptions, headers });
  };
}

export type AsyncResponseData<TData> = {
  responseData?: TData;
  isSuccess: boolean;
  error?: {
    message?: string;
    detail?: string;
    type?: string;
  };
};

export const constructResponseData = async <T>(
  response: Response,
  failureMessage: string,
  options?: {
    defaultResponseData?: T;
    dataType?: "json" | "arrayBuffer";
  }
): Promise<AsyncResponseData<T>> => {
  try {
    if (response.ok && response.status === 204) {
      return {
        isSuccess: true,
        responseData: options?.defaultResponseData,
      };
    } else if (response.ok) {
      const dataType = options?.dataType ? options?.dataType : "json";
      const responseData = await response[dataType]();
      return {
        isSuccess: true,
        responseData: options?.defaultResponseData ?? (responseData as T),
      };
    }

    const { type, detail } = await response.json();

    appInsightsTracker.trackAsyncAPIsFailure(failureMessage, detail);

    return {
      isSuccess: false,
      error: {
        message: failureMessage,
        detail,
        type,
      },
    };
  } catch (error) {
    console.error("Error constructing response data:", error);

    return {
      isSuccess: false,
      error: {
        message: failureMessage,
        detail: "Unknown error during response data construction.",
        type: "ResponseConstructionError",
      },
    };
  }
};

export const fetchWrapper = {
  get: fetchRequest("GET"),
  post: fetchRequest("POST"),
  put: fetchRequest("PUT"),
  delete: fetchRequest("DELETE"),
};

export default fetchWrapper;
